/*
  The MIT License

  Copyright (c) 2017-2019 EclipseSource Munich
  https://github.com/eclipsesource/jsonforms

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { v4 as uuid } from 'uuid';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { FeltComponent } from '@husbanken/designsystem-angular-forms';
import {
  ButtonComponent,
  ButtongroupComponent,
  CalloutComponent,
  IconComponent,
  ModalComponent
} from '@husbanken/designsystem-angular-core';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { toObservable } from '@angular/core/rxjs-interop';
import { CustomDataRenderer, VedleggKravInstance, VedleggService } from "@husbanken/rzrforms-lib";

export enum Filtype {
  PDF = 'pdf',
  JPG = 'jpg',
  JPEG = 'jpeg',
  PNG = 'png',
  GIF = 'gif',
  BMP = 'bmp',
  TIFF = 'tiff',
  DOC = 'doc',
  DOCX = 'docx',
  ODF = 'odf',
  XLS = 'xls',
  XLSX = 'xlsx',
  PPT = 'ppt',
  BIM = 'bim',
}

interface Vedlegg {
  filnavn?: string;
  id?: string;
  kravType?: string;
  stoerellse?: string;
}

@Component({
  selector: 'rzr-vedlegg-krav',
  templateUrl: 'internal.vedleggkrav.renderer.html',
  styleUrl: 'internal.vedleggkrav.renderer.scss',
  imports: [TranslateModule, AsyncPipe, FeltComponent, IconComponent, ButtonComponent, ButtongroupComponent, ModalComponent, CalloutComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalVedleggkravRenderer extends CustomDataRenderer<string[], VedleggKravInstance> implements OnInit {
  @Input() declare elementInstance: VedleggKravInstance;
  private vedleggService = inject(VedleggService);
  private translationService = inject(TranslateService);

  fileVedleggData = signal<Vedlegg[]>([]);
  isDragOver = signal(false);
  isLoading = signal<Record<string, boolean>>({});
  selectedVedleggForDeletion = signal<Vedlegg | null>(null);
  visModal = computed(() => this.selectedVedleggForDeletion() != null);
  visModal$ = toObservable(this.visModal);
  opplastingfeiletMelding = signal('');

  private readonly maksFilstorrelse = 52_428_800; // 50MB
  private readonly godkjenteTyper = Filtype;
  readonly visKunGodkjenteTyper = Object.values(this.godkjenteTyper)
    .map(type => '.' + type)
    .join(',');

  ngOnInit() {
    super.ngOnInit();
    const data = this.elementInstance.data || [];
    data.forEach(vedleggUUID => {
      this.vedleggService
        .getVedlegg({
          formid: this.elementInstance.globalstate.jsonforms.core.data.formid,
          dokid: vedleggUUID,
        })
        .subscribe({
          next: res => {
            this.fileVedleggData.update(prev => [...prev, res]);
            this.isLoading.update(isLoading => ({ ...isLoading, [this.elementInstance.path]: false }));
          },
          error: () => {
            this.isLoading.update(isLoading => ({ ...isLoading, [this.elementInstance.path]: false }));
          },
        });
    });
  }

  upload(file: File, id: string) {
    if (file.size > this.maksFilstorrelse) {
      console.error(`Filen er for stor! Maks størrelse er ${this.maksFilstorrelse / (1024 * 1024)} MB`);
      return;
    } else if (file.size == 0) {
      console.log('Filen har størrelse 0?');
    } else if (!this.erGyldigFiltype(file.name)) {
      console.error('Godkjente typer er:' + this.godkjenteTyper);
    } else {
      console.log('Størrelse og fil er godkjent' + this.maksFilstorrelse + this.godkjenteTyper);
    }

    this.vedleggService
      .addVedlegg({
        formid: this.elementInstance.globalstate.jsonforms.core.data.formid,
        body: {
          vedlegg: {
            id,
            stoerellse: this.getFileSize(file),
            filnavn: file.name,
            kravType: this.elementInstance.uischema.fieldName,
          },
          file: file,
        },
      })
      .subscribe({
        error: e => {
          this.opplastingfeiletMelding.set(_('Vi klarte ikke å laste opp filen. Prøv igjen.'));
          this.fileVedleggData.update(l => l.filter(f => f.id !== id));
        },
        next: v => {
          this.isLoading.update(isLoading => ({ ...isLoading, [id]: false }));
          if (v.status == 'OK') {
            const data = this.elementInstance.data || [];
            const newValue = [...data, v.lagretVedlegg.id];
            this.elementInstance.onChange(newValue);
          } else {
            this.opplastingfeiletMelding.set(this.translationService.instant(v.errormessage));
            this.fileVedleggData.update(l => l.filter(f => f.id !== id));
          }
        },
      });
  }

  private erGyldigFiltype(filnavn: string): boolean {
    const gyldigeFiltyper = Object.values(this.godkjenteTyper);
    console.log(gyldigeFiltyper);
    return gyldigeFiltyper.some(gyldigFiltype => filnavn?.toLowerCase().endsWith(gyldigFiltype));
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    // Sjekk at ny target ikke er inne i dropzone
    const dropZone = event.currentTarget as Element;
    if (!dropZone.contains(event.relatedTarget as Element)) {
      this.isDragOver.set(false);
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    if (event.dataTransfer?.files) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.addFiles(input.files);
    }
    input.value = '';
  }

  addFiles(fileList: FileList) {
    Array.from(fileList).forEach(file => {
      if (file.size > this.maksFilstorrelse) {
        this.opplastingfeiletMelding.set(this.translationService.instant(_('Den valgte filen må være mindre enn ')) + this.maksFilstorrelse / 1024 / 1024 + ' MB.');
        return;
      }

      if (!this.erGyldigFiltype(file.name)) {
        this.opplastingfeiletMelding.set(_('Den valgte filen må være PDF, JPG, BMP, PNG, TIF, DOC, ODF, XLS, PPT eller BIM.'));
        return;
      }
      if (!this.erGyldigFilnavn(file.name)) {
        this.opplastingfeiletMelding.set(_('Vi kunne ikke laste opp den valgte filen fordi filnavnet har tegn vi ikke støtter, som: skråstrek (/), spørsmålstegn (?), kolon (:) og alfakrøll (@). Endre filnavnet slik at det bare har vanlige tegn, som: bokstaver, tall, mellomrom, bindestrek, understrek, punktum, komma og parentes.'));
        return;
      }

      const uniqueId = uuid();
      this.opplastingfeiletMelding.set("");
      this.isLoading.update(isLoading => ({ ...isLoading, [uniqueId]: true }));
      this.fileVedleggData.update(prev => [
        ...prev,
        {
          id: uniqueId,
          filnavn: file.name,
          stoerellse: this.getFileSize(file),
        },
      ]);
      this.upload(file, uniqueId);
    });
  }

  velgForSletting(vedlegg: Vedlegg) {
    console.log(vedlegg);
    this.selectedVedleggForDeletion.set(vedlegg);
    console.log('Valgt for sletting: ' + this.selectedVedleggForDeletion().filnavn);
  }

  slett(file: Vedlegg | null) {
    console.log(file);
    if (!file) return;
    this.vedleggService
      .deleteVedlegg({
        formid: this.elementInstance.globalstate.jsonforms.core.data.formid,
        dokid: file.id,
      })
      .subscribe(() => {
        console.log('Sletter', file);
        this.skjulModal();
      });
    const index = this.fileVedleggData().indexOf(file);
    if (index > -1) {
      this.fileVedleggData.update(prev => prev.filter(f => f.id !== file.id));
      let elementInstanceDataIndex = this.elementInstance.data.findIndex(f => f == file.id);
      this.elementInstance.data.splice(elementInstanceDataIndex, 1);
      this.elementInstance.onChange(this.elementInstance.data);
      // Tvinger gjennom lagring slik at vi ikke risikerer at vedlegget blir slettet, men ligger igjen i skjemadata.
      this.jsonFormsService.forcePersist();
    }
  }

  onClickFileInput(fileInput: HTMLInputElement) {
    fileInput.click();
  }

  getFileSize(file: File) {
    const sizeInKb = file.size / 1024;

    if (sizeInKb > 1024) {
      return `${(sizeInKb / 1024).toFixed(2)} MB`;
    } else {
      return `${sizeInKb.toFixed(2)} KB`;
    }
  }

  skjulModal(): void {
    this.selectedVedleggForDeletion.set(null);
  }

  avbrytSlett() {
    this.skjulModal();
  }

  lukkOpplastingFeilmelding() {
    this.opplastingfeiletMelding.set('');
  }

  private erGyldigFilnavn(name: string) {
    const regex = /^[\w.æøåÆØÅ,()\[\]#\- ]+$/;
    return regex.test(name) && name.length < 200;
  }
}
